<template>
  <!-- Step 1 of the post-signup profile wizard. Mirrors the mobile Name screen
       (iOS OnBoardingNameViewController / Android OnboardingNameFragment),
       which collects first and last name as separate fields. -->
  <div>
    <OnBoardingStepIndicator :current="3" />

    <h2 class="auth-heading">{{ $t('on_boarding.profile_name_page.title') }}</h2>
    <p class="auth-sub">{{ $t('on_boarding.profile_name_page.subtitle') }}</p>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <form @submit.prevent="submit">
      <div class="pro-field mb-3">
        <label class="pro-field__label" for="profileFirstName">
          {{ $t('on_boarding.profile_name_page.first_name_placeholder') }}
        </label>
        <input
          id="profileFirstName"
          ref="firstNameInput"
          v-model="firstName"
          type="text"
          class="form-control form-control-lg pro-field__input"
          :placeholder="$t('on_boarding.profile_name_page.first_name_placeholder')"
          autocomplete="given-name"
          required
        >
      </div>

      <div class="pro-field mb-4">
        <label class="pro-field__label" for="profileLastName">
          {{ $t('on_boarding.profile_name_page.last_name_placeholder') }}
        </label>
        <input
          id="profileLastName"
          v-model="lastName"
          type="text"
          class="form-control form-control-lg pro-field__input"
          :placeholder="$t('on_boarding.profile_name_page.last_name_placeholder')"
          autocomplete="family-name"
          required
        >
      </div>

      <button type="submit" class="btn pro-btn pro-btn--accent w-100" :disabled="!canSubmit">
        <span class="material-icons">arrow_forward</span>
        <span>{{ $t('on_boarding.profile_name_page.continue') }}</span>
      </button>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingStepIndicator from './OnBoardingStepIndicator.vue';
import { isValidNameParts } from '../../../utils/helpers/signupProfile';

export default defineComponent({
  name: 'OnBoardingProfileNamePage',
  components: { OnBoardingStepIndicator },
  data() {
    return {
      // Seeded from the store, which prefills from the Rails record or the
      // identity provider — an SSO user usually only has to confirm.
      firstName: this.$store.state.onBoarding.profileFirstName || '',
      lastName: this.$store.state.onBoarding.profileLastName || '',
    };
  },
  computed: {
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    canSubmit(): boolean {
      return isValidNameParts({ firstName: this.firstName, lastName: this.lastName });
    },
  },
  mounted() {
    (this.$refs.firstNameInput as HTMLInputElement | undefined)?.focus();
  },
  methods: {
    submit() {
      if (!this.canSubmit) {
        this.$store.commit('onBoarding/setError', this.$t('on_boarding.profile_name_page.error_missing'));
        return;
      }
      this.$store.dispatch('onBoarding/submitProfileName', {
        firstName: this.firstName.trim(),
        lastName: this.lastName.trim(),
      });
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>

<style scoped>
.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 0.35rem;
}
.auth-sub {
  color: var(--pro-text-muted);
  font-size: 0.95rem;
  margin: 0 0 1.5rem;
}
</style>
